import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { playCardSnap } from '../../utilities/sounds';
import { showSelectedCards } from '../../redux/gameSlice';
import { useTableGeometryCtx } from './useTableGeometry';
import { fanLayout, fitFan, FAN_FIT_WIDTH } from './cardFan';
import { CardModBadges, OriginalCardFace, cardModsFor } from './CardMods';
import { cardIdAt } from '../../redux/pupSlice';
import './CommunityCards.css';

// Press-and-hold this long on a changed card to peek its ORIGINAL face; a quicker tap still
// selects / picks the card as normal (mirrors PupHand's drag-vs-click threshold).
const HOLD_ORIGINAL_MS = 160;

const suitSymbols = { h: '♥', d: '♦', c: '♣', s: '♠' };
const suitColors  = { h: '#f0245e', d: '#f0245e', c: '#191e4e', s: '#191e4e' };

const DEAL_INTERVAL_MS = 400;
const CARD_WIDTH = 48;
const CARD_HEIGHT = 68;
const CARD_BORDER = 2;   // .card border-width; part of each card's footprint (see fitFan)
const CARD_GAP = 6;
// PLO hero hand: stack the cards tightly so only each card's top-left rank+suit index strip
// peeks out from under the next card (the real-poker corner-index look). Overlap is the px the
// next card slides over the previous (48px wide card → ~18px index strip visible). The fan tilt
// itself is the shared -4°..+4° from cardFan's rotateDeg (no per-hand spread override).
const PLO_OVERLAP = 30;

// Suit Change Power-Up: when the resolve re-pushes the hero's hand with exactly one hole card's
// suit rewritten in place, we DON'T re-run the deal animation — we glitter-crossfade just that
// card, dissolving its old suit/colour out while the new one (already beneath it) phases in.
const SUIT_FX_MS = 1500;

// Index of the single hole card whose SUIT changed in place (same rank, same card count) between
// two hands, or -1 if this isn't a lone suit change. A fresh deal, an appended Power-Up card, or
// a rank shift (Plus/Minus One keeps the suit, changes the rank) all return -1 and keep the
// normal deal fly-in — only Suit Change matches "same rank, different suit, exactly one card".
const detectSuitChange = (prev, next) => {
    if (!prev || prev.length === 0 || prev.length !== next.length) return -1;
    let idx = -1;
    for (let i = 0; i < next.length; i++) {
        if (next[i] !== prev[i]) {
            if (idx !== -1) return -1;   // more than one card differs — not a lone suit change
            idx = i;
        }
    }
    if (idx === -1) return -1;           // nothing differs (shouldn't happen; the key changed)
    const a = prev[idx], b = next[idx];
    return (a.length >= 2 && b.length >= 2
        && a.slice(0, -1) === b.slice(0, -1)   // same rank
        && a.slice(-1) !== b.slice(-1))        // different suit
        ? idx : -1;
};

// highlightIndexes: a Set of POSITIONS in this hand to gold-border at showdown — the winner's best
// five (their hole cards used). null/empty = no highlight. Cards not in the set render normally.
// discardActive/onDiscard: while the hero's own discard Power-Up (e.g. Exchange) waits on its
// pick, every card pulses (.hole-card-discardable) and a click calls onDiscard(card) instead
// of the show-cards selection toggle.
// cardTargetActive/onCardTarget: the same machinery for a card-TARGETING Power-Up (Plus/Minus
// One, Suit Change) picking one of the hero's own hole cards to MODIFY — a click calls
// onCardTarget(card, index) (the server pick wants the 0-based position, not the token).
// peekedBy: { token: [username, ...] } — the hero's own cards an opponent has secretly seen
// (Peek/Peeper). Each such card gets a small, semi-transparent eye (75% up, centered) with a
// hover tooltip naming who saw it. Owner-only by construction: this component renders only the
// hero's own hand.
// wildIndexes: a Set of POSITIONS in THIS hand turned WILD this round, from the authoritative
// game payload (same .card-wild classes as the board — CommunityCards.css is imported here).
// Positions, not tokens: a re-suited card can share a wild card's value without being wild, so
// matching by value would badge — and mislead about — the wrong card.
// A token of literal "XX" is the Half Blind Bandit's masked card — it renders as a
// face-down back among the face-up cards (the server never sends the real token).
// multiDiscardActive/onMultiDiscard: while the hero's own Grand exchange waits on its pick,
// clicks SELECT cards (the show-selection visuals) and a confirm button above the hand
// posts the whole set via onMultiDiscard(cards).
// modifiedBy: { <cardId>: [{ by, original, current, code }, ...] } — the hero's own hole cards a
// value-modifying Power-Up rewrote this round (Plus/Minus One, Suit Change, Set Ace, One Man's
// Trash, Magic Trick). Each such card gets a small "modified" badge (who changed it) plus a
// press-and-hold gesture that peeks its original face. Owner-only by construction (this renders
// only the hero's hand). Keyed by card IDENTITY, so an Exchange or Sacrifice reshaping the hand
// moves each card's history with the card instead of losing it or reattaching it to a neighbour.
// cardIds: this hand's card identities, positionally aligned with `cards`.
const HoleCards = ({ cards, cardIds = null, seat, folded = false, highlightIndexes = null, roomId, stage, wonByFold = false, discardActive = false, onDiscard = null, cardTargetActive = false, onCardTarget = null, peekedBy = null, wildIndexes = null, multiDiscardActive = false, onMultiDiscard = null, modifiedBy = null }) => {
    const { seatAboveStyle } = useTableGeometryCtx();
    const dispatch = useDispatch();
    const key = cards ? cards.join(',') : '';
    // Seed to the cards we mounted with so re-entering a table mid-hand (in-game → lobby →
    // back) shows them immediately. Only a genuinely new hand (the key changes while
    // mounted) re-deals; animating gates the per-card deal animation/sound.
    const [visibleCount, setVisibleCount] = useState(cards ? cards.length : 0);
    const [animating, setAnimating] = useState(false);
    // First index the current deal animation applies to: 0 for a fresh hand, the old hand
    // length when a played Power-Up merely appends card(s) — so only the new card flies in.
    const [dealFrom, setDealFrom] = useState(0);
    // While a Suit Change crossfade is playing: { index, oldCard, nonce }. Drives the fading
    // old-suit overlay + glitter poof on that one card; cleared after SUIT_FX_MS. The nonce
    // re-keys the overlay so back-to-back suit changes on the same card replay the animation.
    const [suitFx, setSuitFx] = useState(null);
    const fxNonceRef = useRef(0);
    // Cards the player has clicked to select, keyed by POSITION. Power-Up rewrites can create two
    // identical tokens in one hand; token-keyed selection would lift/toggle both copies together.
    // Positions remain stable across ordinary stage changes and appended extra cards.
    const [selectedIndexes, setSelectedIndexes] = useState([]);
    // Failure toast for a discard pick + a one-at-a-time guard on the pick request.
    const [discardError, setDiscardError] = useState(null);
    const discardPickRef = useRef(false);
    const prevKeyRef = useRef(key);
    // Guards the one-shot auto-reveal so entering the showdown window fires the "show selected
    // cards" request exactly once per round. Reset on each new deal.
    const revealFiredRef = useRef(false);
    // Press-and-hold on a value-changed card peeks its ORIGINAL face. heldOriginalIndex = the
    // card being peeked (or null); holdTimerRef arms the peek after HOLD_ORIGINAL_MS so a quick
    // tap still selects/picks; suppressClickRef swallows the click that ENDS a hold so releasing
    // never toggles selection / fires a pick.
    const [heldOriginalIndex, setHeldOriginalIndex] = useState(null);
    const holdTimerRef = useRef(null);
    const suppressClickRef = useRef(false);
    useEffect(() => () => { if (holdTimerRef.current) clearTimeout(holdTimerRef.current); }, []);

    useEffect(() => {
        if (!cards || cards.length === 0) {
            setVisibleCount(0);
            setAnimating(false);
            setSelectedIndexes([]);
            setSuitFx(null);
            revealFiredRef.current = false;
            prevKeyRef.current = '';
            return;
        }
        if (key === prevKeyRef.current) {
            // Same cards as the last render / initial mount — show them without re-dealing.
            setVisibleCount(cards.length);
            setAnimating(false);
            return;
        }
        // A played Power-Up only ever APPENDS to the hand we're already showing (the backend
        // appends the drawn card — PupCardService.applyExtraCard). Keep the dealt cards in
        // place and fly in just the new one(s); the .hand-card transition (CommunityCards.css)
        // eases the rest into the grown fan. Selection survives — the hand didn't change,
        // it grew. Anything else (a genuinely new hand) re-deals from scratch.
        const prevCards = prevKeyRef.current ? prevKeyRef.current.split(',') : [];
        // Suit Change Power-Up: the resolve re-pushed the hand with exactly one hole card's suit
        // rewritten (same rank, same count). Don't re-deal — glitter-crossfade just that card:
        // its old suit dissolves out (overlay) while the new suit, already in `cards` beneath it,
        // phases in. Owner-only by construction (this component renders only the hero's hand).
        const suitChangeIndex = detectSuitChange(prevCards, cards);
        if (suitChangeIndex >= 0) {
            prevKeyRef.current = key;
            setVisibleCount(cards.length);
            setAnimating(false);
            fxNonceRef.current += 1;
            setSuitFx({ index: suitChangeIndex, oldCard: prevCards[suitChangeIndex], nonce: fxNonceRef.current });
            const t = setTimeout(() => setSuitFx(null), SUIT_FX_MS);
            return () => clearTimeout(t);
        }
        const extension = prevCards.length > 0 && cards.length > prevCards.length
            && prevCards.every((c, i) => cards[i] === c);
        const from = extension ? prevCards.length : 0;
        prevKeyRef.current = key;
        setSuitFx(null);   // a real deal / appended card — not a suit-change crossfade
        setAnimating(true);
        setDealFrom(from);
        setVisibleCount(from);
        if (!extension) {
            setSelectedIndexes([]);
            revealFiredRef.current = false;
        }
        const timers = [];
        for (let i = from; i < cards.length; i++) {
            timers.push(setTimeout(() => {
                // Monotonic: a discard window may have fast-forwarded the whole hand
                // visible (below) while these timers were still pending.
                setVisibleCount((v) => Math.max(v, i + 1));
                playCardSnap();
            }, (i - from) * DEAL_INTERVAL_MS));
        }
        timers.push(setTimeout(() => setAnimating(false), (cards.length - from) * DEAL_INTERVAL_MS));
        return () => timers.forEach(clearTimeout);
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

    // A live discard/card-target window trumps the deal fly-in: the server's 5s pick clock
    // is already burning (these cards are start-phase-only, so a window can land while the
    // deal animation is still running — worst on the deep PLO hands), so show every card at
    // once and let clicks through immediately instead of pulsing cards that would swallow
    // the pick.
    useEffect(() => {
        if ((discardActive || cardTargetActive || multiDiscardActive) && cards && cards.length > 0) {
            setVisibleCount(cards.length);
            setAnimating(false);
        }
    }, [discardActive, cardTargetActive, multiDiscardActive]); // eslint-disable-line react-hooks/exhaustive-deps

    // When the determine-winner (showdown) window opens with cards still selected, auto-reveal
    // exactly those cards to the table. Only for players who aren't auto-revealed in full anyway:
    // folded players, or a last-one-standing (won-by-fold) win — the backend enforces the same
    // rule. Fires once per round (revealFiredRef, reset on each new deal). Kept ABOVE the early
    // return below so the hook order stays stable when the hand is empty.
    useEffect(() => {
        const selectedCards = selectedIndexes
            .map((index) => cards?.[index])
            .filter((card) => card && card !== 'XX');
        if (stage === 'showdown' && !revealFiredRef.current
            && selectedCards.length > 0 && (folded || wonByFold) && roomId != null) {
            revealFiredRef.current = true;
            dispatch(showSelectedCards({ roomId, cards: selectedCards }));
        }
    }, [stage, selectedIndexes, cards, folded, wonByFold, roomId, dispatch]);

    if (!cards || cards.length === 0) return null;

    const totalCards = cards.length;
    // >2 cards (PLO) get the tight, centered fan from cardFan.js, blanketing the avatar. The
    // 2-card Hold'em fan itself is left to CSS (.hand-card :first/:last-child) and the original
    // even-gap startX math, so the fan stays pixel-for-pixel unchanged.
    const multi = totalCards > 2;
    // Constrain a 4+ card fan to the info bubble: raise overlap for 4/5 cards, shrink the card for
    // 6+ (fit.scale < 1). fanLayout is fed the fitted cardWidth/overlap so the deal fly-in still
    // lands flush. n<=3 (and the 2-card CSS path) get scale 1 / unchanged overlap.
    const fit = multi ? fitFan(totalCards, CARD_WIDTH, CARD_BORDER, FAN_FIT_WIDTH, PLO_OVERLAP) : null;
    const layout = multi ? fanLayout(totalCards, fit.cardWidth, { overlap: fit.overlap }) : null;
    const containerCenter = multi
        ? layout.containerCenter
        : (totalCards * CARD_WIDTH + (totalCards - 1) * CARD_GAP) / 2;

    // For a shrunk (6+) fan, size the card + border + fonts inline to match fit.cardWidth (the
    // width fanLayout used). scale === 1 emits nothing, keeping n<=5 pixel-identical to the CSS.
    const shrunk = multi && fit.scale !== 1;
    const cardSizeStyle = shrunk ? {
        width: `${fit.cardWidth}px`,
        height: `${fit.cardWidth * (CARD_HEIGHT / CARD_WIDTH)}px`,
        borderWidth: `${CARD_BORDER * fit.scale}px`,
    } : null;
    const fontStyle = (base) => (shrunk ? { fontSize: `${base * fit.scale}px` } : undefined);

    // Both layouts center the row over the avatar AND pin its bottom edge into the info bubble
    // (anchorBottom + hideBottomEdge, so the bubble overlaps the cards' bottom edge) — the look the
    // 2-card hand has always had. A 4+ card PLO fan uses the SAME bottom-tuck so it sits low enough
    // to overlap the bubble too, instead of floating raised above it with a gap. Its width is still
    // constrained to the bubble by fitFan above; the 2-card path is byte-identical to before.
    const positionStyle = seatAboveStyle(seat, { center: true, anchorBottom: true, hideBottomEdge: true });

    // Once folded, grey out + fade the hand so it reads as "out of the round" but stays
    // readable. Never apply it while the deal animation is running so a fresh hand always
    // deals in clean (folded resets to false at the start of a new round anyway).
    const foldedClass = folded && !animating ? ' hole-cards-folded' : '';

    // Click a card to toggle its selection (ignored mid-deal so the reveal animation isn't
    // disturbed). Each card selects independently and stays selected across stages.
    // While a discard pick is live, the click IS the pick — selection is untouched, and the
    // pick fires even mid-animation (its server window is burning). A failed pick (network,
    // or the pick raced the window's timeout) shows a toast so the player knows to re-click
    // or that the window is gone — a silent failure here costs the card for the round.
    const toggleCard = (card, index) => {
        // A press-and-hold to peek the original just ended on this card — swallow the click it
        // produced so releasing doesn't also toggle selection / fire a pick.
        if (suppressClickRef.current) { suppressClickRef.current = false; return; }
        // The Half Blind Bandit's masked back is inert — there is nothing to select or pick.
        if (card === 'XX') return;
        if (discardActive) {
            if (!onDiscard || discardPickRef.current) return;
            discardPickRef.current = true;
            Promise.resolve(onDiscard(card))
                .catch((e) => {
                    setDiscardError(typeof e === 'string' && e ? e : 'Could not discard that card');
                    setTimeout(() => setDiscardError(null), 4000);
                })
                .finally(() => { discardPickRef.current = false; });
            return;
        }
        // A card-target pick works exactly like a discard pick (same window, same one-shot
        // guard, same toast), but hands the server the card's 0-based position.
        if (cardTargetActive) {
            if (!onCardTarget || discardPickRef.current) return;
            discardPickRef.current = true;
            Promise.resolve(onCardTarget(card, index))
                .catch((e) => {
                    setDiscardError(typeof e === 'string' && e ? e : 'Could not pick that card');
                    setTimeout(() => setDiscardError(null), 4000);
                })
                .finally(() => { discardPickRef.current = false; });
            return;
        }
        if (animating) return;
        setSelectedIndexes((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
    };

    // Press-and-hold gesture for peeking a changed card's original face. Armed only when the
    // card actually HAS changes and no pick window is live (there a click means "pick", so a
    // hold must not steal it). After HOLD_ORIGINAL_MS the original overlay shows; releasing
    // clears it and swallows the trailing click. A quick tap never arms — selection is untouched.
    const pickActive = discardActive || cardTargetActive || multiDiscardActive;
    const startHoldOriginal = (e, index, hasMods) => {
        if (!hasMods || pickActive) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return; // left button only
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* older browsers */ }
        if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
        holdTimerRef.current = setTimeout(() => {
            holdTimerRef.current = null;
            suppressClickRef.current = true;
            setHeldOriginalIndex(index);
        }, HOLD_ORIGINAL_MS);
    };
    const endHoldOriginal = () => {
        if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
        setHeldOriginalIndex((cur) => (cur == null ? cur : null));
    };

    // The card's rendered footprint (base 48x68, or the shrunk size for 6+ card fans). A selected
    // card lifts by half its own length (height) and grows very slightly (+1w / +2h).
    const baseCardW = shrunk ? fit.cardWidth : CARD_WIDTH;
    const baseCardH = shrunk ? fit.cardWidth * (CARD_HEIGHT / CARD_WIDTH) : CARD_HEIGHT;

    return (
        <div className={`hole-cards${foldedClass}`} style={positionStyle}>
            {/* One "Show" tag, centered above the whole hand, whenever any card is selected —
                a heads-up that the selected cards will be shown to the table at determine-winner.
                Suppressed while a multi-discard pick is live: there, selection means SWAP. */}
            {selectedIndexes.length > 0 && !multiDiscardActive && <div className="show-label">Show</div>}
            {/* Grand exchange confirm: the selected cards post as ONE swap set. */}
            {multiDiscardActive && (
                <button
                    type="button"
                    className="multi-discard-confirm"
                    disabled={selectedIndexes.length === 0 || discardPickRef.current}
                    onClick={() => {
                        if (!onMultiDiscard || discardPickRef.current || selectedIndexes.length === 0) return;
                        const selectedCards = selectedIndexes
                            .map((index) => cards[index])
                            .filter((card) => card && card !== 'XX');
                        if (selectedCards.length === 0) return;
                        discardPickRef.current = true;
                        Promise.resolve(onMultiDiscard(selectedCards))
                            .then(() => setSelectedIndexes([]))
                            .catch((e) => {
                                setDiscardError(typeof e === 'string' && e ? e : 'Could not exchange those cards');
                                setTimeout(() => setDiscardError(null), 4000);
                            })
                            .finally(() => { discardPickRef.current = false; });
                    }}
                >
                    {selectedIndexes.length === 0 ? 'Select cards to swap'
                        : `Swap ${selectedIndexes.length} card${selectedIndexes.length > 1 ? 's' : ''}`}
                </button>
            )}
            {discardError && <div className="discard-error">{discardError}</div>}
            {cards.slice(0, visibleCount).map((card, i) => {
                // The Half Blind Bandit's masked card ("XX"): a plain face-down back among
                // the face-up cards — same fly-in/tilt wrapper, no rank/suit, no clicks.
                if (card === 'XX') {
                    const cardCenterM = multi
                        ? layout.cards[i].naturalCenterX
                        : i * (CARD_WIDTH + CARD_GAP) + CARD_WIDTH / 2;
                    const startXM = containerCenter - cardCenterM;
                    const wrapperStyleM = multi
                        ? { transform: `rotate(${layout.cards[i].rotate}deg)`, marginLeft: `${layout.cards[i].marginLeft}px` }
                        : {};
                    return (
                        <div key={i} className="hand-card" style={wrapperStyleM}>
                            <div
                                className={`card card-back${animating && i >= dealFrom ? ' hole-card-dealing' : ''}`}
                                style={{
                                    '--deal-start-x': `${startXM}px`,
                                    '--deal-start-y': '-28vh',
                                    ...cardSizeStyle,
                                }}
                            />
                        </div>
                    );
                }
                const rank = card.slice(0, -1);
                const suit = card.slice(-1);
                // This card is mid Suit-Change crossfade: overlay its OLD suit/colour, fading out.
                const showSuitFx = suitFx != null && suitFx.index === i;
                const fxOldSuit = showSuitFx ? suitFx.oldCard.slice(-1) : null;
                // startX shifts each card from the row center to its natural resting position so
                // the fly-in lands exactly where the resting fan puts it (no jump at deal end).
                const cardCenter = multi
                    ? layout.cards[i].naturalCenterX
                    : i * (CARD_WIDTH + CARD_GAP) + CARD_WIDTH / 2;
                const startX = containerCenter - cardCenter;
                const selected = selectedIndexes.includes(i);
                // Names of opponents who secretly saw THIS card (Peek/Peeper). Present → eye badge.
                const peekedNames = peekedBy && peekedBy[card];
                // Value changes a Power-Up made to THIS card this round (Plus/Minus One, etc.),
                // self-healed against the live token. Present → "modified" badges + hold-to-peek.
                const cardMods = cardModsFor(modifiedBy, cardIdAt(cardIds, i));
                const holdingOriginal = heldOriginalIndex === i && cardMods && cardMods.length > 0;
                // For a multi-card fan the wrapper carries the inline rotation + negative margin
                // (overriding the 2-card CSS rules); the inner .card keeps the deal transform. A
                // selected card is raised above its neighbours so its lift never tucks under them.
                const wrapperStyle = {
                    ...(multi
                        ? { transform: `rotate(${layout.cards[i].rotate}deg)`, marginLeft: `${layout.cards[i].marginLeft}px` }
                        : {}),
                    ...(selected ? { zIndex: 20 } : {}),
                };
                return (
                    <div
                        key={i}
                        className="hand-card"
                        style={wrapperStyle}
                        onClick={() => toggleCard(card, i)}
                        onPointerDown={(e) => startHoldOriginal(e, i, !!(cardMods && cardMods.length))}
                        onPointerUp={endHoldOriginal}
                        onPointerLeave={endHoldOriginal}
                        onPointerCancel={endHoldOriginal}
                    >
                        <div
                            className={`card card-face${animating && i >= dealFrom ? ' hole-card-dealing' : ''}${highlightIndexes?.has(i) ? ' card-winner' : ''}${selected ? ' card-selected' : ''}${discardActive || cardTargetActive ? ' hole-card-discardable' : ''}${wildIndexes?.has(i) ? ' card-wild' : ''}`}
                            style={{
                                color: suitColors[suit] || '#191e4e',
                                '--deal-start-x': `${startX}px`,
                                '--deal-start-y': '-28vh',
                                ...cardSizeStyle,
                                // Selected: grow slightly and lift up its own tilt axis (the wrapper's
                                // rotation rotates this translateY). translateY(-half its length).
                                ...(selected ? {
                                    width: `${baseCardW + 1}px`,
                                    height: `${baseCardH + 2}px`,
                                    transform: `translateY(-${baseCardH / 2}px)`,
                                } : {}),
                            }}
                        >
                            <div className="card-rank" style={fontStyle(16)}>{rank}</div>
                            <div className="card-suit" style={fontStyle(14)}>{suitSymbols[suit] || suit}</div>
                            <div className="card-rank-big" style={fontStyle(36)}>{rank}</div>
                            {wildIndexes?.has(i) && <div className="card-wild-badge">WILD</div>}
                            {showSuitFx && (
                                <>
                                    {/* Suit Change: a copy of the card showing the OLD suit + colour,
                                        pinned over the new one and dissolving out over 1.5s so the new
                                        suit phases in beneath. .card-face gives it the same background +
                                        padding + alignment, so its glyphs land pixel-aligned over the
                                        real ones — only the suit symbol + red/black colour crossfade. */}
                                    <div
                                        key={`sfx-old-${suitFx.nonce}`}
                                        className="suit-fx-old card-face"
                                        style={{ color: suitColors[fxOldSuit] || '#191e4e' }}
                                        aria-hidden="true"
                                    >
                                        <div className="card-rank" style={fontStyle(16)}>{rank}</div>
                                        <div className="card-suit" style={fontStyle(14)}>{suitSymbols[fxOldSuit] || fxOldSuit}</div>
                                        <div className="card-rank-big" style={fontStyle(36)}>{rank}</div>
                                    </div>
                                    {/* A poof of glitter over the transforming card. */}
                                    <div key={`sfx-glitter-${suitFx.nonce}`} className="suit-fx-glitter" aria-hidden="true" />
                                </>
                            )}
                            {peekedNames && peekedNames.length > 0 && (
                                // Owner-only "eye": this card was secretly revealed to an opponent.
                                // Anchored to the card (centered, 75% up). stopPropagation so reading
                                // the tooltip never toggles select / discard / card-target on the card.
                                <span
                                    className="hole-card-eye"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M2 12 C 5 6.5, 19 6.5, 22 12 C 19 17.5, 5 17.5, 2 12 Z" />
                                        <circle cx="12" cy="12" r="3.2" />
                                        <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
                                    </svg>
                                    <span className="hole-card-eye-tip">
                                        This card has been revealed to {peekedNames.join(', ')}
                                    </span>
                                </span>
                            )}
                            {/* Press-and-hold overlay: this card's ORIGINAL face (before any
                                change), shown while the owner holds the card down. */}
                            {holdingOriginal && (
                                <OriginalCardFace token={cardMods[0].original} fontStyle={fontStyle} />
                            )}
                            {/* "Modified" badges: one per value change (Plus/Minus One, Suit
                                Change, …), side by side, each naming who did it on hover. */}
                            {cardMods && cardMods.length > 0 && <CardModBadges mods={cardMods} />}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default HoleCards;
